import { useState, useEffect } from 'react';
import { FieldRow, QcFlagDisplay } from '../../shared';
import styles from './FeesPrizesSection.module.css';

export interface FeesPrizesData {
  entry_fee: number | null;
  entry_fee_currency: string | null;
  cost_range: string | null;
  prize_structure: any | null;
  prizes_per_category: number | null;
  prize_types: string[] | null;
  total_win_opportunities: number | null;
  has_tiered_prizes: boolean | null;
  primary_currency: string | null;
  extra_benefits: string | null;
  qc_flag_fees_prizes: boolean | null;
  qc_flag_fees_prizes_notes: string | null;
}

export interface FeesPrizesSectionProps {
  data: FeesPrizesData;
  editable: boolean;
  onChange?: (updates: Partial<FeesPrizesData>) => void;
}

export function FeesPrizesSection({ data, editable, onChange }: FeesPrizesSectionProps) {
  const [localData, setLocalData] = useState<FeesPrizesData>(data);
  const [prizeStructureDraft, setPrizeStructureDraft] = useState<string>(() =>
    data.prize_structure ? JSON.stringify(data.prize_structure, null, 2) : ''
  );
  const [prizeStructureError, setPrizeStructureError] = useState<string | null>(null);

  useEffect(() => {
    setLocalData(data);
    setPrizeStructureDraft(data.prize_structure ? JSON.stringify(data.prize_structure, null, 2) : '');
    setPrizeStructureError(null);
  }, [data]);

  const handleFieldChange = <K extends keyof FeesPrizesData>(field: K, value: FeesPrizesData[K]) => {
    const updates = { ...localData, [field]: value } as FeesPrizesData;
    setLocalData(updates);
    onChange?.(updates);
  };

  const handleQcChange = (flag: boolean, notes: string) => {
    const updates = {
      ...localData,
      qc_flag_fees_prizes: flag,
      qc_flag_fees_prizes_notes: notes
    };
    setLocalData(updates);
    onChange?.(updates);
  };

  return (
    <div className={styles.container}>
      <div className={styles.fieldsContainer}>
      <FieldRow
        label="Entry Fee"
        value={localData.entry_fee}
        editable={editable}
        onChange={(v) => {
          const numValue: number | null = v === '' ? null : Number(v);
          handleFieldChange('entry_fee', numValue);
        }}
      />
      <FieldRow
        label="Entry Fee Currency"
        value={localData.entry_fee_currency}
        editable={editable}
        onChange={(v) => handleFieldChange('entry_fee_currency', v)}
      />
      <FieldRow
        label="Primary Currency"
        value={localData.primary_currency}
        editable={editable}
        onChange={(v) => handleFieldChange('primary_currency', v)}
      />
      <FieldRow
        label="Cost Range"
        value={localData.cost_range}
        editable={editable}
        onChange={(v) => handleFieldChange('cost_range', v)}
      />
      <FieldRow
        label="Prizes per Category"
        value={localData.prizes_per_category}
        editable={editable}
        onChange={(v) => {
          const numValue: number | null = v === '' ? null : Number(v);
          handleFieldChange('prizes_per_category', numValue);
        }}
      />
      <FieldRow
        label="Prize Types"
        value={localData.prize_types ? localData.prize_types.join(', ') : null}
        editable={editable}
        onChange={(v) => {
          const arrayValue: string[] | null = v
            ? v.split(',').map((s) => s.trim()).filter(Boolean)
            : null;
          handleFieldChange('prize_types', arrayValue);
        }}
        multiline
      />
      <FieldRow
        label="Total Win Opportunities"
        value={localData.total_win_opportunities}
        editable={editable}
        onChange={(v) => {
          const numValue: number | null = v === '' ? null : Number(v);
          handleFieldChange('total_win_opportunities', numValue);
        }}
      />
      <FieldRow
        label="Has Tiered Prizes"
        value={localData.has_tiered_prizes === null ? '' : (localData.has_tiered_prizes ? 'Yes' : 'No')}
        editable={editable}
        onChange={(v) => {
          const lower = v.trim().toLowerCase();
          const boolValue: boolean | null = lower === 'yes' ? true : lower === 'no' ? false : null;
          handleFieldChange('has_tiered_prizes', boolValue);
        }}
      />
      <FieldRow
        label="Extra Benefits"
        value={localData.extra_benefits}
        editable={editable}
        onChange={(v) => handleFieldChange('extra_benefits', v)}
        multiline
      />
      <div className={styles.prizeStructureBlock}>
        <label className={styles.prizeStructureLabel} htmlFor="fees-prize-structure">
          Prize Structure
        </label>
        {editable ? (
          <>
            <textarea
              id="fees-prize-structure"
              className={styles.prizeStructureTextarea}
              value={prizeStructureDraft}
              onChange={(event) => {
                setPrizeStructureDraft(event.target.value);
                setPrizeStructureError(null);
              }}
              onBlur={() => {
                const trimmed = prizeStructureDraft.trim();
                if (!trimmed) {
                  handleFieldChange('prize_structure', null);
                  return;
                }

                try {
                  const parsed = JSON.parse(prizeStructureDraft);
                  handleFieldChange('prize_structure', parsed);
                  setPrizeStructureError(null);
                } catch (_error) {
                  setPrizeStructureError('Invalid JSON. Please provide valid JSON before leaving the field.');
                }
              }}
              placeholder="Enter JSON describing the prize structure"
              rows={6}
            />
            {prizeStructureError ? <p className={styles.prizeStructureError}>{prizeStructureError}</p> : null}
          </>
        ) : (
          <pre className={styles.prizeStructureReadonly}>
            {localData.prize_structure ? JSON.stringify(localData.prize_structure, null, 2) : 'No prize structure provided.'}
          </pre>
        )}
      </div>

      <QcFlagDisplay
        flagged={localData.qc_flag_fees_prizes}
        notes={localData.qc_flag_fees_prizes_notes}
        editable={editable}
        onChange={handleQcChange}
      />
      </div>
    </div>
  );
}

export default FeesPrizesSection;
